import { useState, FunctionComponent, useEffect } from 'react';
import CertificatesApi from '../../api/certificates';
import { Skill, License } from '../../types/certificates';
import { AddCircle } from '../../components/icons';
import { SelectObject } from '../../types/certificates';
import { IconButton, Input, Snackbar, Dialog, Select, Table, TableCell, TableRow } from '../../tailwindcomponents';
import EditColumn from '../Security/EditColumn';
import { palette } from '../../theme/palette';

type LicenseProps = {
    setLoading: Function,
    isAdmin: boolean
}


const SkillManagement: FunctionComponent<LicenseProps> = ({ setLoading, isAdmin }) => {
    const [maintenerSkills, setMaintenerSkills] = useState<Skill[]>([]);
    const [mxiSkills, setMxiSkills] = useState<string[]>([]);

    const [createSkillDialog, setCreateSkillDialog] = useState<boolean>(false);
    const [updateSkillObj, setUpdateSkillObj] = useState<Skill | null>(null);
    const [newSkill, setNewSkill] = useState<{ skill_code: string, skill_description: string }>({ skill_code: "", skill_description: "" });

    const [editRow, setEditRow] = useState<number>(-1);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'warning' | 'error'>('success');

    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<boolean>(false);
    const [skillToDelete, setSkillToDelete] = useState<Skill | null>(null);

    const editSkillCodes = updateSkillObj && !mxiSkills.includes(updateSkillObj.skill_code)
        ? [updateSkillObj.skill_code, ...mxiSkills]
        : mxiSkills;
    const editSkillOptions: SelectObject[] = editSkillCodes.map((skill, index) => ({ name: skill, id: index }));
    const [relatedLicenses, setRelatedLicenses] = useState<License[]>([]);

    useEffect(() => {
        refresh();
    }, []);

    const refresh = async () => {
        clearstate();
        setLoading(true);

        try {
            const [mxiRes, maintenerRes] = await Promise.all([
                CertificatesApi.GetMxiSkills(),
                CertificatesApi.GetMaintenerSkills()
            ]);

            if (!mxiRes.status) {
                setMxiSkills([]);
                setSnackbarMessage("Failed to fetch MXI skills");
                setSnackbarSeverity("error");
                return;
            }

            if (!maintenerRes.status) {
                setMaintenerSkills([]);
                setSnackbarMessage("Failed to fetch Maintener skills");
                setSnackbarSeverity("error");
                return;
            }

            setMaintenerSkills(maintenerRes.data);

            // filter unused MXI skills
            const usedSkillCodes = maintenerRes.data.map((s: any) => s.skill_code);
            const mxiCodes = mxiRes.data;
            const unusedMxiSkillCodes = mxiCodes.filter(v => !usedSkillCodes.includes(v)).map(c=> c); // MXI codes that are not yet used in licenses            
            setMxiSkills(unusedMxiSkillCodes);

        } catch (err) {
            setSnackbarMessage("Something went wrong while fetching skills");
            setSnackbarSeverity("error");
        } finally {
            setLoading(false);
        }
    };

    const clearstate = () => {
        setEditRow(-1);
        setUpdateSkillObj(null);
        setNewSkill({ skill_code: "", skill_description: "" });
        setCreateSkillDialog(false);
    }

    const deleteSkill = async (index: number) => {
        const skill = maintenerSkills[index - 1];
        setSkillToDelete(skill);

        try {
            const res = await CertificatesApi.GetAllLicenses();
            if (res.status) {
                const related = res.data.filter(
                    (license) => license.skills?.some((ls) => ls.skill_id === skill.id)
                );
                setRelatedLicenses(related);
            } else {
                setRelatedLicenses([]);
            }
        } catch {
            setRelatedLicenses([]);
        }

        setDeleteConfirmDialog(true);
    }

    const confirmDeleteSkill = () => {
        if (!skillToDelete) return;
        CertificatesApi.DeleteSkill(skillToDelete.id).then((res) => {
            if (res.status === false) {
                setSnackbarMessage("Failed to delete skill");
                setSnackbarSeverity('error');
                clearstate();
            }
            else {
                setSnackbarMessage("Successfully deleted skill");
                setSnackbarSeverity('success');
                refresh();
            }
        });
        setDeleteConfirmDialog(false);
        setSkillToDelete(null);
        setRelatedLicenses([]);
    }

    const cancelDeleteSkill = () => {
        setDeleteConfirmDialog(false);
        setSkillToDelete(null);
        setRelatedLicenses([]);
    }

    const updateSkill = () => {
        if (updateSkillObj !== null) {
            CertificatesApi.UpdateSkill(updateSkillObj).then((res) => {
                if (res.status === true) {
                    setSnackbarMessage("Successfully updated skill");
                    setSnackbarSeverity('success');
                    refresh();
                }
                else {
                    setSnackbarMessage("Failed to update skill");
                    setSnackbarSeverity('error');
                }
            });
        }
    }

    const onCloseDialog = (status: boolean) => {
        if (status === false) {
            clearstate();
            return;
        }
        else {
            CertificatesApi.CreateSkill(newSkill.skill_code, newSkill.skill_description).then((res) => {
                if (res.status === true) {
                    setSnackbarMessage("Successfully created skill");
                    setSnackbarSeverity('success');
                    refresh();
                }
                else {
                    setSnackbarMessage("Failed to create skill");
                    setSnackbarSeverity('error');
                }
            });
        }
    }

    const generateTableBody = (row: Skill, index: number) => {
        return (
            <TableRow>
                <TableCell>{index}</TableCell>
                <TableCell>{editRow === index ?
                    <Select
                        selectedItem={editSkillOptions.findIndex(skill => skill.name === (updateSkillObj ? updateSkillObj.skill_code : ""))}
                        width={300}
                        filter_by_text={"Skills"}
                        optionsObject={editSkillOptions}
                        onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            const selectedName = editSkillOptions[Number(e.target.value)]?.name;
                            if (selectedName) setUpdateSkillObj(prev => prev ? { ...prev, skill_code: selectedName } : null);
                        }}
                    >
                    </Select>
                    :
                    row.skill_code
                }
                </TableCell>
                <TableCell>{editRow === index ?
                    <Input
                        multiline={true}
                        help_text={"Edit description"}
                        label_text={"Edit description"}
                        value={updateSkillObj ? updateSkillObj.skill_description : row.skill_description}
                        onTextChange={(text: string) => setUpdateSkillObj(prev => prev ? { ...prev, skill_description: text } : null)}
                        type="text"
                        width={100}
                    /> : row.skill_description}
                </TableCell>
                {isAdmin ?
                    <EditColumn
                        abort={true}
                        index={index}
                        saveChanges={() => updateSkill()}
                        deleteRow={() => deleteSkill(index)}
                        editRow={editRow}
                        setEditRow={
                            editRow === index ? () => { clearstate(); } :
                                () => {
                                    setEditRow(index);
                                    setUpdateSkillObj(maintenerSkills[index - 1]);
                                }
                        }
                    />
                    :
                    <TableCell></TableCell>
                }
            </TableRow>
        )
    }

    const generateDeleteDialogTableBody = (row: License, index: number) => {
        return (
            <TableRow key={index}>
                <TableCell>{row.license_code}</TableCell>
                <TableCell>{row.license_description || '-'}</TableCell>
            </TableRow>
        )
    }

    return (
        <div>
            <div>
                <div className="flex flex-col items-center mt-7">
                    {(!createSkillDialog && isAdmin === true) &&
                        <IconButton onClick={() => setCreateSkillDialog(true)} style={{ color: palette.success }} >
                            <AddCircle />
                        </IconButton>
                    }
                </div>
                <Table
                    headers={["skill code", "description", ""]}
                    rows={maintenerSkills}
                    generateBody={(row: Skill, index: number) => generateTableBody(row, index)}
                    defaultRowsPerPage={25}
                />
            </div>
            <Dialog
                visible={createSkillDialog}
                max_width={"md"} title={"Create new skill"}
                context={
                    <div>
                        <Select
                            selectedItem={newSkill ? mxiSkills.findIndex(skill => skill === newSkill.skill_code) : -1}
                            width={200}
                            filter_by_text={"Skill Codes"}
                            optionsObject={mxiSkills.map((skill, index) => ({ name: skill, id: index }))}
                            onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => setNewSkill(prev => ({ ...prev, skill_code: mxiSkills[Number(e.target.value)] }))}
                        >
                        </Select>
                        <div className="flex flex-row flex-wrap items-center justify-between mt-2.5">
                            <Input
                                multiline={true}
                                help_text={"Skill description"}
                                label_text={"Skill description"}
                                value={newSkill ? newSkill.skill_description : ""}
                                onTextChange={(text: string) => setNewSkill(prev => prev && { ...prev, skill_description: text })}
                                type="text"
                                width={100}
                            />
                        </div>
                    </div>
                }
                onClose={(status: boolean) => onCloseDialog(status)}
                strict={false}
                show_options={true}
            />
            <Dialog
                visible={deleteConfirmDialog}
                max_width={"md"}
                title={"Confirm skill deletion"}
                context={
                    <div>
                        <p className="mb-4">
                            Are you sure you want to delete skill <strong>{skillToDelete?.skill_code}</strong>?
                        </p>
                        {relatedLicenses.length > 0 ? (
                            <div>
                                <p className="mb-2">This skill will be removed from the following {relatedLicenses.length} license(s):</p>
                                <Table
                                    skipIndexCol={true}
                                    headers={["License Code", "Description"]}
                                    rows={relatedLicenses}
                                    generateBody={(row: License, index: number) => generateDeleteDialogTableBody(row, index)}
                                />
                            </div>
                        ) : (
                            <p>This skill is not associated with any licences.</p>
                        )}
                    </div>
                }
                onClose={(status: boolean) => {
                    if (status) {
                        confirmDeleteSkill();
                    } else {
                        cancelDeleteSkill();
                    }
                }}
                strict={true}
                show_options={true}
            />
            <Snackbar
                onSnackBarClose={() => setSnackbarMessage("")}
                open={snackbarMessage.length > 0}
                message={snackbarMessage}
                autoHide={true}
                horizontalPosition='right'
                severity={snackbarSeverity}
            ></Snackbar>
        </div>
    )
}
export default SkillManagement;